import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { CreatePermissionTagForm } from './CreatePermissionTagForm';
import { useCreatePermissionTag } from './useCreatePermissionTag';
import type { CreatePermissionTagProps } from '../types';

interface CreatePermissionTagButtonProps extends
  CreatePermissionTagProps,
  WithDataTestid,
  Omit<ButtonProps, 'onClick'> {}

const displayName = 'CreatePermissionTagButton';
const testid = buildTestid(displayName);

function CreatePermissionTagButton({
  onCreatePermissionTag = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreatePermissionTagButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });
  const createPermissionTag = useCreatePermissionTag({
    onCreatePermissionTag,
    onSuccess: modal.close,
  });

  return (
    <React.Fragment>
      {modal.wrap(
        <CreatePermissionTagForm
          isPending={createPermissionTag.isPending}
          onCancel={modal.close}
          onSubmit={createPermissionTag.onSubmit}
        />,
      )}

      <Button
        icon={<PlusIcon />}
        onClick={modal.open}
        {...testid(dataTestId)}
        {...props}
      >
        {t('features:create_permission_tag.title')}
      </Button>
    </React.Fragment>
  );
}

CreatePermissionTagButton.displayName = displayName;

export { CreatePermissionTagButton };
